import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Paper,
  Typography,
} from "@mui/material";
import LSLogo from "../images/StackedLogo.svg";

import { useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { isEmailRegex, urlRoutes } from "../constants/page";
import { apiGetAuth, loginUser } from "../calls/apiCalls";
import { setUser } from "../redux/user";
import { GET_MY_PERMISSIONS } from "../constants/apiUrls";
import { ensureCatalog } from "../redux/companyCatalogs";
import { setPermissions } from "../redux/permissions";


const welcomeText = "Welcome To LinkStep";
const loginTest = "Please login to get started";

const emptyLogin = {
  email: "",
  password: "",
};

const loginError = {
  email: false,
  password: false,
};

export default function Login() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [userObj, setUserObj] = useState(emptyLogin);
  const [loginErrors, setLoginErrors] = useState("");
  const [errorObj, setErrorObj] = useState(loginError);
  const location = useLocation();
  const navigate = useNavigate();

  const validateSignupData = () => {
    let errors = false;

    if (!isEmailRegex.test(userObj.email)) {
      setErrorObj((prevState) => ({
        ...prevState,
        email: true,
      }));
      errors = true;
    } else {
      setErrorObj((prevState) => ({
        ...prevState,
        email: false,
      }));
    }
    if (userObj.password.length < 8) {
      setErrorObj((prevState) => ({
        ...prevState,
        password: true,
      }));
      errors = true;
    } else {
      setErrorObj((prevState) => ({
        ...prevState,
        password: false,
      }));
    }
    return errors;
  };

  const getSetPermissions = async () => {
    const response = await apiGetAuth(GET_MY_PERMISSIONS);
    if (!response || typeof response !== "object") return "Success";

    dispatch(setPermissions(response));
    const user = response?.userDto ? response?.userDto : {};
    dispatch(setUser(user));
    return "Success";
  };

  const handleChangeUser = (e) => {
    const { name, value } = e.target;
    setUserObj((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleLogin = async (e) => {
    e?.preventDefault?.();

    const errors = validateSignupData();
    if (errors) return;

    setLoading(true);
    setLoginErrors("");

    try {
      const response = await loginUser(userObj);

      if (response?.uuid) {
        await getSetPermissions();
        dispatch(ensureCatalog("groups"));

        if (
          location.pathname === urlRoutes.login ||
          location.pathname === urlRoutes.splash
        ) {
          navigate(urlRoutes.dashboard);
        }
        return;
      }

      const message = response?.response?.data;

      if (message === "Wrong Email or Password" || message === "error") {
        setLoginErrors(message);
        return;
      }

      if (message === "Inactive account") {
        setLoginErrors(
          "This account is inactive. If this is an error please contact your administrator to reactivate your account.",
        );
        return;
      }

      setLoginErrors("Network Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        minHeight: "85vh",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Paper
        elevation={3}
        sx={{
          padding: 2,
          display: "flex",
          flexDirection: "column",
          width: "330px",
          backgroundColor: "#eee",
        }}
      >
        <Box
          sx={{
            marginBottom: "px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 2,
            }}
          >
            <img
              src={LSLogo}
              style={{
                height: "120px",
              }}
              alt="LinkStep Logo"
            />
          </Box>
          <Typography
            variant="h6"
            sx={{ textAlign: "center", fontWeight: 700 }}
          >
            {welcomeText}
          </Typography>
          <Typography
            variant="body2"
            color={loginErrors ? "error" : "black"}
            sx={{ textAlign: "center", mt: 1, mb: 2 }}
          >
            {loginErrors || loginTest}
          </Typography>
        </Box>
        <Box component="form" onSubmit={handleLogin} sx={{ marginX: 1 }}>
          <FormControl
            sx={{ width: "100%", my: 2 }}
            variant="outlined"
            size="small"
          >
            <InputLabel htmlFor="outlined-adornment-email">Email</InputLabel>
            <OutlinedInput
              id="outlined-adornment-email"
              name="email"
              onChange={handleChangeUser}
              error={errorObj.email}
              label="Email"
            />
          </FormControl>
          <FormControl sx={{ width: "100%" }} variant="outlined" size="small">
            <InputLabel htmlFor="outlined-adornment-password">
              Password
            </InputLabel>
            <OutlinedInput
              id="outlined-adornment-password"
              name="password"
              onChange={handleChangeUser}
              error={errorObj.password}
              type={showPassword ? "text" : "password"}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showPassword
                        ? "hide the password"
                        : "display the password"
                    }
                    onClick={() => setShowPassword((show) => !show)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              label="Password"
            />
          </FormControl>
        </Box>
        <Box
          style={{
            display: "flex",
            marginTop: 16,
            paddingLeft: 8,
            paddingRight: 8,
            marginBottom: 8,
            flexDirection: "column",
          }}
        >
          <Button
            sx={{ marginBottom: 3 }}
            variant="contained"
            fullWidth
            type="submit"
            disabled={loading}
            onClick={handleLogin}
          >
            {loading ? (
              <CircularProgress size="20px" color="inherit" />
            ) : (
              "Login"
            )}
          </Button>
          <Link style={{ textAlign: "center" }} to={urlRoutes.forgotPassword}>
            Forgot Password?
          </Link>
        </Box>
      </Paper>
    </Box>
  );
}
